import React, { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import BusinessCard from '../components/BusinessCard';
import FilterSidebar from '../components/FilterSidebar';
import { getBusinesses, getCategories, getLocations } from '../api';
import { Search, ChevronLeft, ChevronRight } from 'lucide-react';

export default function ArchivePage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [businesses, setBusinesses] = useState([]);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(1);
  const [loading, setLoading] = useState(true);
  const [categories, setCategories] = useState([]);
  const [locations, setLocations] = useState(null);
  const [searchInput, setSearchInput] = useState(searchParams.get('q') || '');

  const currentPage = parseInt(searchParams.get('page') || '1', 10);
  const q = searchParams.get('q') || '';
  const categoria = searchParams.get('categoria') || '';
  const citta = searchParams.get('citta') || '';
  const provincia = searchParams.get('provincia') || '';
  const regione = searchParams.get('regione') || '';
  const featured = searchParams.get('featured') || '';

  useEffect(() => {
    getCategories().then(r => setCategories(r.data)).catch(() => {});
    getLocations().then(r => setLocations(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    setLoading(true);
    const params = { page: currentPage, limit: 12 };
    if (q) params.q = q;
    if (categoria) params.categoria = categoria;
    if (citta) params.citta = citta;
    if (provincia) params.provincia = provincia;
    if (regione) params.regione = regione;
    if (featured === 'true') params.featured = true;
    getBusinesses(params)
      .then(r => {
        setBusinesses(r.data.items);
        setTotal(r.data.total);
        setPages(r.data.pages);
      })
      .catch(() => { setBusinesses([]); })
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const handleSearch = (e) => {
    e.preventDefault();
    const p = new URLSearchParams(searchParams);
    if (searchInput.trim()) p.set('q', searchInput.trim()); else p.delete('q');
    p.set('page', '1');
    setSearchParams(p);
  };

  const handleApplyFilters = (f) => {
    const p = new URLSearchParams();
    if (q) p.set('q', q);
    if (f.categoria) p.set('categoria', f.categoria);
    if (f.citta) p.set('citta', f.citta);
    if (f.provincia) p.set('provincia', f.provincia);
    if (f.regione) p.set('regione', f.regione);
    p.set('page', '1');
    setSearchParams(p);
  };

  const handleResetFilters = () => {
    const p = new URLSearchParams();
    if (q) p.set('q', q);
    setSearchParams(p);
  };

  const goPage = (pg) => {
    const p = new URLSearchParams(searchParams);
    p.set('page', String(pg));
    setSearchParams(p);
    window.scrollTo(0, 0);
  };

  const hasFilters = categoria || citta || provincia || regione || featured;

  return (
    <div style={{ backgroundColor: 'var(--mi-bg)' }}>
      <Header />
      <div className="mi-container py-10">
        {/* Page header */}
        <div className="mb-8">
          <h1 className="font-display text-3xl md:text-4xl" style={{ color: 'var(--mi-fg)' }}>
            {q ? `Risultati per “${q}”` : 'Tutte le Attività'}
          </h1>
          {total > 0 && (
            <p className="mt-1 text-sm" style={{ color: 'var(--mi-text)' }}>{total} attività trovate</p>
          )}
        </div>

        {/* Search bar */}
        <form onSubmit={handleSearch} className="mb-6">
          <div className="relative max-w-xl">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2" style={{ color: 'var(--mi-text)' }} />
            <input
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              placeholder="Cerca per nome, categoria, servizio..."
              className="w-full h-11 pl-9 pr-28 rounded-xl border text-sm"
              style={{ borderColor: 'var(--mi-border)', backgroundColor: '#fff', color: 'var(--mi-fg)', outline: 'none' }}
            />
            <button
              type="submit"
              className="absolute right-2 top-1/2 -translate-y-1/2 h-8 px-3 rounded-lg text-xs font-medium"
              style={{ backgroundColor: 'var(--mi-fg)', color: 'var(--mi-bg)' }}
            >
              Cerca
            </button>
          </div>
        </form>

        {/* Active filters display */}
        {hasFilters && (
          <div className="flex flex-wrap gap-2 mb-4">
            {[categoria, citta, provincia, regione, featured === 'true' ? 'In evidenza' : null]
              .filter(Boolean)
              .map((f) => (
                <span
                  key={f}
                  className="text-xs px-2 py-1 rounded-full"
                  style={{ backgroundColor: 'rgba(200,169,110,0.15)', color: 'var(--mi-accent)' }}
                >
                  {f}
                </span>
              ))}
          </div>
        )}

        {/* Main layout */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          {/* Sidebar */}
          <div className="lg:col-span-3">
            <FilterSidebar
              filters={{ categoria, citta, provincia, regione }}
              categories={categories}
              locations={locations}
              onApply={handleApplyFilters}
              onReset={handleResetFilters}
            />
          </div>

          {/* Results */}
          <div className="lg:col-span-9">
            {loading ? (
              <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
                {[...Array(9)].map((_, i) => (
                  <div key={i} className="rounded-2xl overflow-hidden border" style={{ borderColor: 'var(--mi-border)' }}>
                    <div className="skeleton-shimmer" style={{ paddingTop: '56.25%' }} />
                    <div className="p-4 space-y-2">
                      <div className="skeleton-shimmer h-5 rounded w-3/4" />
                      <div className="skeleton-shimmer h-3 rounded w-1/2" />
                      <div className="skeleton-shimmer h-3 rounded w-full" />
                    </div>
                  </div>
                ))}
              </div>
            ) : businesses.length === 0 ? (
              <div className="text-center py-20 rounded-2xl border" style={{ borderColor: 'var(--mi-border)', backgroundColor: '#fff' }}>
                <p className="font-display text-2xl mb-2" style={{ color: 'var(--mi-fg)' }}>Nessun risultato, ma siamo vicini.</p>
                <p className="text-sm" style={{ color: 'var(--mi-text)' }}>Prova a cambiare parola chiave o amplia l'area geografica.</p>
                <button
                  onClick={handleResetFilters}
                  className="mt-4 px-4 h-9 rounded-lg text-sm border"
                  style={{ borderColor: 'var(--mi-border)', color: 'var(--mi-fg)' }}
                >
                  Rimuovi filtri
                </button>
              </div>
            ) : (
              <>
                <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
                  {businesses.map((b) => (
                    <BusinessCard key={b._id} business={b} />
                  ))}
                </div>

                {/* Pagination */}
                {pages > 1 && (
                  <div className="flex items-center justify-center gap-2 mt-10">
                    <button
                      onClick={() => goPage(currentPage - 1)}
                      disabled={currentPage <= 1}
                      className="p-2 rounded-lg border disabled:opacity-40"
                      style={{ borderColor: 'var(--mi-border)' }}
                    >
                      <ChevronLeft size={16} />
                    </button>
                    {Array.from({ length: Math.min(pages, 7) }, (_, i) => {
                      let pg;
                      if (pages <= 7) pg = i + 1;
                      else if (currentPage <= 4) pg = i + 1;
                      else if (currentPage >= pages - 3) pg = pages - 6 + i;
                      else pg = currentPage - 3 + i;
                      return (
                        <button
                          key={pg}
                          onClick={() => goPage(pg)}
                          className="w-9 h-9 rounded-lg text-sm font-medium border"
                          style={{
                            borderColor: pg === currentPage ? 'var(--mi-fg)' : 'var(--mi-border)',
                            backgroundColor: pg === currentPage ? 'var(--mi-fg)' : '#fff',
                            color: pg === currentPage ? 'var(--mi-bg)' : 'var(--mi-fg)',
                          }}
                        >
                          {pg}
                        </button>
                      );
                    })}
                    <button
                      onClick={() => goPage(currentPage + 1)}
                      disabled={currentPage >= pages}
                      className="p-2 rounded-lg border disabled:opacity-40"
                      style={{ borderColor: 'var(--mi-border)' }}
                    >
                      <ChevronRight size={16} />
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
}
